/**
 * Turns the signup response's `attendanceUrl` (the "custom link" Spond Core
 * returns for a confirmed public-event signup — also emailed to the attendee)
 * into a same-origin Discover attendance-entry href.
 *
 * We deliberately trust only the raw attendance TOKEN carried by that URL and
 * rebuild the destination from our own locale/eventId/basePath. That keeps the
 * redirect same-origin (no open-redirect via a backend-controlled URL) and works
 * whether Core hands back the token as a `?token=` query param or as the final
 * path segment of the Core attendance URL (`…/attendance/{token}`).
 *
 * The client can't set the HTTP-only `spond_attendance` cookie itself, so the
 * href points at the `/attendance/enter` route handler, which validates the
 * token, sets the cookie, and redirects to the clean attendance view.
 */
export function attendanceEnterHref(opts: {
  locale: string;
  eventId: string;
  attendanceUrl: string;
  basePath?: string;
}): string | null {
  const token = extractAttendanceToken(opts.attendanceUrl);
  if (!token) return null;
  const basePath = opts.basePath ?? "";
  return `${basePath}/${opts.locale}/events/${encodeURIComponent(
    opts.eventId,
  )}/attendance/enter?token=${encodeURIComponent(token)}`;
}

/** Reads the attendance token from Core's `attendanceUrl`; null when absent. */
function extractAttendanceToken(rawUrl: string): string | null {
  if (!rawUrl) return null;
  let url: URL;
  try {
    // A base lets `new URL` parse relative values too; the base is discarded.
    url = new URL(rawUrl, "https://discover.invalid");
  } catch {
    return null;
  }

  const queryToken = url.searchParams.get("token");
  if (queryToken) return queryToken;

  const segments = url.pathname.split("/").filter(Boolean);
  const last = segments.at(-1);
  const prev = segments.at(-2);
  // Token is expected in `…/attendance/{token}`; ignore unrelated URL shapes.
  if (!last || prev !== "attendance" || last === "enter") return null;
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}
